import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { storefrontApi } from "../services/storefrontApi";
import { getCartStorageKey } from "../lib/cart";
import { storefrontDevLog } from "../lib/devLog";
import type { StorefrontBranch, StorefrontCategory } from "../types/storefront";
import { useStorefront } from "./StorefrontContext";

interface BranchState {
  branch: StorefrontBranch | null;
  categories: StorefrontCategory[];
  showSelector: boolean;
  requestBranchChange: (branch: StorefrontBranch) => void;
  confirmBranchChange: () => void;
  cancelBranchChange: () => void;
  openSelector: () => void;
}

const BranchContext = createContext<BranchState | null>(null);

export function BranchProvider({ children }: { children: React.ReactNode }) {
  const { slug, branches, categories: initialCategories } = useStorefront();
  const [branch, setBranch] = useState<StorefrontBranch | null>(null);
  const [pendingBranch, setPendingBranch] = useState<StorefrontBranch | null>(null);
  const [showSelector, setShowSelector] = useState(false);
  const [categories, setCategories] = useState(initialCategories);

  useEffect(() => {
    if (!slug || !branches.length) return;
    const savedId = localStorage.getItem(`elmor-storefront-branch:${slug}`);
    const saved = branches.find((item) => item.id === savedId);
    const next = saved ?? (branches.length === 1 ? branches[0] : null);
    setBranch(next);
    setShowSelector(!next);
    storefrontDevLog("Resolved branch", {
      tenant: slug,
      branchId: next?.id || null,
      branchName: next?.name || null,
      source: saved ? "local-storage" : branches.length === 1 ? "only-branch" : "selection-required"
    });
  }, [slug, branches]);

  useEffect(() => {
    if (!slug || !branch) return;
    storefrontApi.getStorefrontCategories(slug, branch.id).then((nextCategories) => {
      setCategories(nextCategories);
      storefrontDevLog("Categories returned", {
        tenant: slug,
        branchId: branch.id,
        count: nextCategories.length
      });
    }).catch(() => undefined);
  }, [slug, branch]);

  const select = useCallback(
    (next: StorefrontBranch) => {
      if (!slug) return;
      setBranch(next);
      setPendingBranch(null);
      setShowSelector(false);
      localStorage.setItem(`elmor-storefront-branch:${slug}`, next.id);
      window.dispatchEvent(new CustomEvent("elmor:branch-changed", { detail: next.id }));
    },
    [slug]
  );

  const value = useMemo(
    () => ({
      branch,
      categories,
      showSelector,
      requestBranchChange: (next: StorefrontBranch) => {
        const hasCart = Boolean(
          slug && localStorage.getItem(getCartStorageKey(slug, branch?.id ?? "none"))
        );
        if (hasCart && branch?.id !== next.id) setPendingBranch(next);
        else select(next);
      },
      confirmBranchChange: () => pendingBranch && select(pendingBranch),
      cancelBranchChange: () => setPendingBranch(null),
      openSelector: () => setShowSelector(true),
      pendingBranch
    }),
    [branch, categories, pendingBranch, select, showSelector, slug]
  );

  return (
    <BranchContext.Provider value={value as BranchState}>
      {children}
      {showSelector && branches.length > 1 && (
        <BranchSelector branches={branches} onSelect={select} />
      )}
      {pendingBranch && (
        <ConfirmBranchChange
          branch={pendingBranch}
          onConfirm={() => select(pendingBranch)}
          onCancel={() => setPendingBranch(null)}
        />
      )}
    </BranchContext.Provider>
  );
}

function BranchSelector({
  branches,
  onSelect
}: {
  branches: StorefrontBranch[];
  onSelect: (branch: StorefrontBranch) => void;
}) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="modal-panel max-w-3xl" role="dialog" aria-modal="true" aria-labelledby="branch-title">
        <div className="modal-heading">
          <div>
            <p className="eyebrow">Delivery area</p>
            <h2 id="branch-title">Choose your nearest branch</h2>
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {branches.map((item) => (
            <button className="branch-card" key={item.id} onClick={() => onSelect(item)}>
              <strong>{item.name}</strong>
              <span>{item.address}</span>
              <span>{item.estimatedDeliveryTime}</span>
              <small>{item.deliveryEnabled ? "Delivery available" : "Pickup only"}</small>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function ConfirmBranchChange({
  branch,
  onConfirm,
  onCancel
}: {
  branch: StorefrontBranch;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="modal-backdrop">
      <section className="modal-panel max-w-lg" role="alertdialog" aria-modal="true">
        <h2>Change to {branch.name}?</h2>
        <p className="mt-2 text-store-muted">
          Your cart will be revalidated for this branch. Unavailable items may be removed and prices may change.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <button className="button-secondary" onClick={onCancel}>Keep branch</button>
          <button className="button-primary" onClick={onConfirm}>Change branch</button>
        </div>
      </section>
    </div>
  );
}

export function useBranch() {
  const value = useContext(BranchContext);
  if (!value) throw new Error("useBranch must be used inside BranchProvider.");
  return value;
}
